import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
  useMemo
} from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";

import api from "../../services/api";

import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  Typography
} from "@mui/material";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { CircularProgress } from "@material-ui/core";
import messageNode from "./nodes/messageNode.js";

import "reactflow/dist/style.css";

import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  onElementsRemove,
  useReactFlow
} from "react-flow-renderer";
import FlowBuilderAddTextModal from "../../components/FlowBuilderAddTextModal";
import FlowBuilderIntervalModal from "../../components/FlowBuilderIntervalModal";
import startNode from "./nodes/startNode";
import conditionNode from "./nodes/conditionNode";
import menuNode from "./nodes/menuNode";
import intervalNode from "./nodes/intervalNode";
import imgNode from "./nodes/imgNode";
import randomizerNode from "./nodes/randomizerNode";
import videoNode from "./nodes/videoNode";
import FlowBuilderConditionModal from "../../components/FlowBuilderConditionModal";
import FlowBuilderMenuModal from "../../components/FlowBuilderMenuModal";
import {
  AccessTime,
  CallSplit,
  Close,
  DynamicFeed,
  Image,
  ImportExport,
  LibraryBooks,
  MapOutlined,
  Message,
  MicNone,
  RocketLaunch,
  Videocam
} from "@mui/icons-material";
import RemoveEdge from "./nodes/removeEdge";
import FlowBuilderAddImgModal from "../../components/FlowBuilderAddImgModal";
import FlowBuilderTicketModal from "../../components/FlowBuilderAddTicketModal";
import FlowBuilderAddAudioModal from "../../components/FlowBuilderAddAudioModal";
import audioNode from "./nodes/audioNode";
import { useNodeStorage } from "../../stores/useNodeStorage";
import FlowBuilderRandomizerModal from "../../components/FlowBuilderRandomizerModal";
import FlowBuilderAddVideoModal from "../../components/FlowBuilderAddVideoModal";
import FlowBuilderSingleBlockModal from "../../components/FlowBuilderSingleBlockModal";
import singleBlockNode from "./nodes/singleBlockNode";
import ticketNode from "./nodes/ticketNode";
import { ConfirmationNumber } from "@material-ui/icons";
import flowBuilderRoomGrid from "../../assets/flowbuilder-room-grid.svg";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    position: "relative",
    backgroundColor: "#06162c",
    overflow: "hidden",
    border: "1px solid rgba(93, 170, 252, 0.18)",
    borderRadius: 0,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    fontFamily: "Inter, Roboto, sans-serif",
    ...theme.scrollbarStyles,
    "& .react-flow__node": {
      fontFamily: "Inter, Roboto, sans-serif",
      filter: "drop-shadow(0 18px 28px rgba(0,0,0,0.34))"
    },
    "& .react-flow__node > div:first-child": {
      position: "relative",
      overflow: "visible !important",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(240,247,255,0.98) 100%) !important",
      border: "1px solid rgba(93, 170, 252, 0.46) !important",
      borderRadius: "12px !important",
      boxShadow:
        "0 18px 30px rgba(0, 7, 26, 0.34), 0 8px 0 rgba(0, 90, 229, 0.08), inset 0 1px 0 rgba(255,255,255,0.96), inset 0 -1px 0 rgba(0,90,229,0.12) !important",
      transform: "translateZ(0)",
      transition: "box-shadow 160ms ease, transform 160ms ease"
    },
    "& .react-flow__node > div:first-child:hover": {
      transform: "translateY(-1px)",
      boxShadow:
        "0 22px 36px rgba(0, 7, 26, 0.40), 0 9px 0 rgba(0, 90, 229, 0.10), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(0,90,229,0.14) !important"
    },
    "& .react-flow__node > div:first-child::before": {
      content: '""',
      position: "absolute",
      inset: 1,
      borderRadius: 11,
      pointerEvents: "none",
      background:
        "linear-gradient(145deg, rgba(255,255,255,0.42), rgba(93,170,252,0.03) 46%, rgba(0,90,229,0.10))"
    },
    "& .react-flow__node > div:first-child > div:not(.react-flow__handle)": {
      position: "relative",
      zIndex: 1
    },
    "& .react-flow__handle": {
      position: "absolute !important",
      width: "24px !important",
      height: "24px !important",
      minWidth: "24px !important",
      minHeight: "24px !important",
      display: "flex !important",
      alignItems: "center",
      justifyContent: "center",
      background:
        "linear-gradient(145deg, #1d7cff 0%, #005AE5 56%, #003fb8 100%) !important",
      border: "2px solid rgba(219,226,255,0.96) !important",
      borderRadius: "999px !important",
      opacity: "1 !important",
      zIndex: "8 !important",
      boxShadow:
        "0 8px 14px rgba(0, 16, 56, 0.36), 0 0 0 4px rgba(0,90,229,0.16), 0 0 12px rgba(0,240,255,0.42), inset 0 1px 0 rgba(255,255,255,0.34) !important",
      transform: "translateY(-50%) !important",
      transition: "none !important"
    },
    "& .react-flow__handle-left": {
      left: "-13px !important"
    },
    "& .react-flow__handle-right": {
      right: "-13px !important"
    },
    "& .react-flow__handle::before": {
      content: '""',
      position: "absolute",
      inset: "-7px",
      borderRadius: "999px"
    },
    "& .react-flow__handle-right::after": {
      content: '">"',
      color: "#ffffff",
      fontSize: 14,
      fontWeight: 800,
      lineHeight: "14px",
      pointerEvents: "none",
      textShadow: "0 1px 4px rgba(0,0,0,0.35)"
    },
    "& .react-flow__handle-left::after": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "999px",
      background: "#ffffff",
      boxShadow: "0 0 8px rgba(255,255,255,0.85)"
    },
    "& .react-flow__handle svg": {
      display: "none !important"
    },
    "& .react-flow__handle:hover": {
      transform: "translateY(-50%) !important",
      boxShadow:
        "0 8px 14px rgba(0, 16, 56, 0.36), 0 0 0 4px rgba(0,90,229,0.16), 0 0 12px rgba(0,240,255,0.42), inset 0 1px 0 rgba(255,255,255,0.34) !important"
    },
    "& .react-flow__node-start .react-flow__handle-right, & .react-flow__node-message .react-flow__handle-right, & .react-flow__node-singleBlock .react-flow__handle-right, & .react-flow__node-ticket .react-flow__handle-right, & .react-flow__node-interval .react-flow__handle-right, & .react-flow__node-img .react-flow__handle-right, & .react-flow__node-audio .react-flow__handle-right, & .react-flow__node-video .react-flow__handle-right": {
      top: "50% !important"
    },
    "& .react-flow__node-menu .react-flow__handle-right": {
      right: "-24px !important"
    },
    "& .react-flow__connection-path": {
      stroke: "#00f0ff",
      strokeWidth: 5,
      filter: "drop-shadow(0 0 7px rgba(0,240,255,0.82))",
      strokeLinecap: "round"
    },
    "& .react-flow__edge-path": {
      stroke: "#00d9ff",
      strokeWidth: 4,
      filter: "drop-shadow(0 0 5px rgba(0,217,255,0.58))"
    },
    "& .react-flow__edge.selected .react-flow__edge-path": {
      stroke: "#00f0ff",
      strokeWidth: 5,
      filter: "drop-shadow(0 0 9px rgba(0,240,255,0.76))"
    },
    "& .react-flow__controls": {
      overflow: "hidden",
      borderRadius: 10,
      border: "1px solid rgba(93,170,252,0.22)",
      background: "rgba(4, 18, 38, 0.90)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 16px 32px rgba(0,0,0,0.32)"
    },
    "& .react-flow__controls button": {
      backgroundColor: "rgba(9, 30, 58, 0.90)",
      color: "#ffffff !important",
      borderBottom: "1px solid rgba(93,170,252,0.16)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)"
    },
    "& .react-flow__controls button:hover": {
      backgroundColor: "rgba(0, 90, 229, 0.88)"
    },
    "& .react-flow__controls button svg": {
      color: "#ffffff !important",
      fill: "#ffffff !important",
      stroke: "#ffffff !important"
    },
    "& .react-flow__controls button svg path": {
      fill: "#ffffff !important",
      stroke: "#ffffff !important"
    },
    "& .react-flow__minimap": {
      borderRadius: 10,
      overflow: "hidden",
      border: "1px solid rgba(93,170,252,0.22)",
      background: "rgba(3, 15, 33, 0.92) !important",
      boxShadow: "0 16px 32px rgba(0,0,0,0.30)"
    }
  },
  miniMapPanel: {
    position: "absolute",
    right: 24,
    bottom: 24,
    zIndex: 12,
    width: 190,
    padding: "10px 10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(93,170,252,0.30)",
    background:
      "linear-gradient(180deg, rgba(4,18,38,0.94) 0%, rgba(7,25,52,0.88) 100%)",
    backdropFilter: "blur(12px)",
    boxShadow:
      "0 18px 34px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.10)",
    "& .react-flow__minimap": {
      position: "static !important",
      width: "170px !important",
      height: "104px !important",
      margin: "8px 0 0 0 !important",
      borderRadius: "8px !important",
      border: "1px solid rgba(93,170,252,0.26) !important",
      background: "rgba(2, 13, 30, 0.86) !important",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04) !important"
    },
    "& .react-flow__minimap-mask": {
      fill: "rgba(0, 15, 38, 0.46) !important"
    },
    "& .react-flow__minimap-node": {
      fill: "#5DAAFC !important",
      stroke: "#00f0ff !important",
      strokeWidth: "2px !important"
    }
  },
  miniMapHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.02em",
    textTransform: "uppercase"
  },
  miniMapLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6
  },
  miniMapClose: {
    width: 24,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(93,170,252,0.22)",
    borderRadius: 8,
    color: "#ffffff",
    background: "rgba(8, 28, 56, 0.72)",
    cursor: "pointer",
    padding: 0
  },
  miniMapToggle: {
    position: "absolute",
    right: 24,
    bottom: 24,
    zIndex: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 38,
    padding: "0 14px",
    border: "1px solid rgba(93,170,252,0.32)",
    borderRadius: 999,
    color: "#ffffff",
    background:
      "linear-gradient(145deg, rgba(0,90,229,0.96), rgba(0,43,118,0.92))",
    boxShadow:
      "0 14px 26px rgba(0,0,0,0.36), 0 0 18px rgba(0,90,229,0.26), inset 0 1px 0 rgba(255,255,255,0.18)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.02em",
    textTransform: "uppercase"
  },
  speeddial: {
    backgroundColor: "red"
  }
}));

function geraStringAleatoria(tamanho) {
  var stringAleatoria = "";
  var caracteres =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (var i = 0; i < tamanho; i++) {
    stringAleatoria += caracteres.charAt(
      Math.floor(Math.random() * caracteres.length)
    );
  }
  return stringAleatoria;
}

const nodeTypes = {
  message: messageNode,
  start: startNode,
  condition: conditionNode,
  menu: menuNode,
  interval: intervalNode,
  img: imgNode,
  audio: audioNode,
  randomizer: randomizerNode,
  video: videoNode,
  singleBlock: singleBlockNode,
  ticket: ticketNode
};

const edgeTypes = {
  buttonedge: RemoveEdge
};

const initialNodes = [
  {
    id: "1",
    position: { x: 250, y: 100 },
    data: { label: "Inicio do fluxo" },
    type: "start"
  }
];

const initialEdges = [];

const FLOW_DELETE_KEY_CODES = ["Backspace", "Delete"];

const FLOW_CONNECTION_LINE_STYLE = {
  stroke: "#00f0ff",
  strokeWidth: 5,
  strokeLinecap: "round",
  filter: "drop-shadow(0 0 7px rgba(0,240,255,0.82))"
};

const FLOW_CANVAS_STYLE = {
  backgroundImage: `linear-gradient(180deg, rgba(0,12,32,0.18), rgba(0,8,24,0.42)), url(${flowBuilderRoomGrid})`,
  backgroundSize: "100% 100%",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "#06162c"
};

const FLOW_DEFAULT_EDGE_OPTIONS = {
  style: {
    stroke: "#00d9ff",
    strokeWidth: 4,
    filter: "drop-shadow(0 0 5px rgba(0,217,255,0.58))"
  },
  animated: false
};

const FLOW_DEFAULT_NODE_STYLE = {
  backgroundColor: "#13111C",
  padding: 0,
  borderRadius: 8
};

const FLOW_SELECTED_NODE_STYLE = {
  backgroundColor: "#0000FF",
  padding: 1,
  borderRadius: 8
};

const hasSameNodeStyle = (node, style) =>
  node.style?.backgroundColor === style.backgroundColor &&
  node.style?.padding === style.padding &&
  node.style?.borderRadius === style.borderRadius;

export const FlowBuilderConfig = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const storageAction = useNodeStorage(state => state.action);
  const storageNode = useNodeStorage(state => state.node);
  const setNodesStorage = useNodeStorage(state => state.setNodesStorage);
  const setAct = useNodeStorage(state => state.setAct);

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [dataNode, setDataNode] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [modalAddText, setModalAddText] = useState(null);
  const [modalAddInterval, setModalAddInterval] = useState(false);
  const [modalAddCondition, setModalAddCondition] = useState(null);
  const [modalAddMenu, setModalAddMenu] = useState(null);
  const [modalAddImg, setModalAddImg] = useState(null);
  const [modalAddAudio, setModalAddAudio] = useState(null);
  const [modalAddRandomizer, setModalAddRandomizer] = useState(null);
  const [modalAddVideo, setModalAddVideo] = useState(null);
  const [modalAddSingleBlock, setModalAddSingleBlock] = useState(null);
  const [modalAddTicket, setModalAddTicket] = useState(null);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [miniMapOpen, setMiniMapOpen] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const addNode = useCallback((type, data = {}) => {
    if (type === "start") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old.filter(item => item.id !== "1"),
          {
            id: "1",
            position: { x: posX, y: posY },
            data: { label: "Inicio do fluxo" },
            type: "start"
          }
        ];
      });
    }
    if (type === "text") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { label: data.text },
            type: "message"
          }
        ];
      });
    }
    if (type === "interval") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { label: `Intervalo ${data.sec} seg.`, sec: data.sec },
            type: "interval"
          }
        ];
      });
    }
    if (type === "condition") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: {
              key: data.key,
              condition: data.condition,
              value: data.value
            },
            type: "condition"
          }
        ];
      });
    }
    if (type === "menu") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: {
              message: data.message,
              arrayOption: data.arrayOption
            },
            type: "menu"
          }
        ];
      });
    }
    if (type === "img") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { url: data.url },
            type: "img"
          }
        ];
      });
    }
    if (type === "audio") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { url: data.url, record: data.record },
            type: "audio"
          }
        ];
      });
    }
    if (type === "randomizer") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { percent: data.percent },
            type: "randomizer"
          }
        ];
      });
    }
    if (type === "video") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { url: data.url },
            type: "video"
          }
        ];
      });
    }
    if (type === "singleBlock") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "singleBlock"
          }
        ];
      });
    }

    if (type === "ticket") {
      return setNodes(old => {
        const lastNode = old[old.length - 1] || initialNodes[0];
        const posY = lastNode.position?.y || 100;
        const posX = (lastNode.position?.x || 0) + (lastNode.width || 240) + 40;

        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "ticket"
          }
        ];
      });
    }


  }, [setNodes]);

  const textAdd = useCallback(data => {
    addNode("text", data);
  }, [addNode]);

  const intervalAdd = useCallback(data => {
    addNode("interval", data);
  }, [addNode]);

  const conditionAdd = useCallback(data => {
    addNode("condition", data);
  }, [addNode]);

  const menuAdd = useCallback(data => {
    addNode("menu", data);
  }, [addNode]);

  const imgAdd = useCallback(data => {
    addNode("img", data);
  }, [addNode]);

  const audioAdd = useCallback(data => {
    addNode("audio", data);
  }, [addNode]);

  const randomizerAdd = useCallback(data => {
    addNode("randomizer", data);
  }, [addNode]);

  const videoAdd = useCallback(data => {
    addNode("video", data);
  }, [addNode]);

  const singleBlockAdd = useCallback(data => {
    addNode("singleBlock", data);
  }, [addNode]);

  const ticketAdd = useCallback(data => {
    addNode("ticket", data)
  }, [addNode]);

  useEffect(() => {
    let isActive = true;

    setLoading(true);

    const fetchFlow = async () => {
      try {
        const { data } = await api.get(`/flowbuilder/flow/${id}`);
        if (!isActive) return;

        if (data.flow.flow !== null) {
          setNodes(data.flow.flow.nodes);
          setEdges(data.flow.flow.connections);
        }
      } catch (err) {
        if (isActive) {
          toastError(err);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchFlow();

    return () => {
      isActive = false;
    };
  }, [id, setEdges, setNodes]);

  useEffect(() => {
    if (storageAction === "delete") {
      setNodes(old => old.filter(item => item.id !== storageNode));
      setEdges(old => {
        const newData = old.filter(item => item.source !== storageNode);
        const newClearTarget = newData.filter(
          item => item.target !== storageNode
        );
        return newClearTarget;
      });
      setNodesStorage("");
      setAct("idle");
      return;
    }
    if (storageAction === "duplicate") {
      setNodes(old => {
        const nodeDuplicate = old.find(item => item.id === storageNode);
        if (!nodeDuplicate) return old;

        const maiorX = old.reduce(
          (max, node) => Math.max(max, node.position?.x || 0),
          0
        );
        const lastNode = old[old.length - 1] || nodeDuplicate;
        const nodeNew = {
          ...nodeDuplicate,
          id: geraStringAleatoria(30),
          position: {
            x: maiorX + 240,
            y: lastNode.position?.y || nodeDuplicate.position?.y || 100
          },
          selected: false,
          style: FLOW_DEFAULT_NODE_STYLE
        };
        return [...old, nodeNew];
      });
      setNodesStorage("");
      setAct("idle");
    }
  }, [storageAction, storageNode, setAct, setEdges, setNodes, setNodesStorage]);

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const onConnect = useCallback(
    params => setEdges(eds => addEdge(params, eds)),
    [setEdges]
  );

  const saveFlow = useCallback(async () => {
    await api
      .post("/flowbuilder/flow", {
        idFlow: id,
        nodes: nodes,
        connections: edges
      })
      .then(res => {
        toast.success("Fluxo salvo com sucesso");
      });
  }, [edges, id, nodes]);

  const doubleClick = useCallback((event, node) => {
    setDataNode(node);
    if (node.type === "message") {
      setModalAddText("edit");
    }
    if (node.type === "interval") {
      setModalAddInterval("edit");
    }
    if (node.type === "condition") {
      setModalAddCondition("edit");
    }
    if (node.type === "menu") {
      setModalAddMenu("edit");
    }
    if (node.type === "img") {
      setModalAddImg("edit");
    }
    if (node.type === "audio") {
      setModalAddAudio("edit");
    }
    if (node.type === "randomizer") {
      setModalAddRandomizer("edit");
    }
    if (node.type === "singleBlock") {
      setModalAddSingleBlock("edit");
    }
    if (node.type === "ticket") {
      setModalAddTicket("edit")
    }
  }, []);

  const clickNode = useCallback((event, node) => {
    setNodes(old =>
      old.map(item => {
        const nextStyle =
          item.id === node.id ? FLOW_SELECTED_NODE_STYLE : FLOW_DEFAULT_NODE_STYLE;

        if (item.style === nextStyle || hasSameNodeStyle(item, nextStyle)) {
          return item;
        }

        return {
          ...item,
          style: nextStyle
        };
      })
    );
  }, [setNodes]);
  const clickEdge = useCallback((event, node) => {
    setNodes(old =>
      old.map(item => {
        if (
          item.style === FLOW_DEFAULT_NODE_STYLE ||
          hasSameNodeStyle(item, FLOW_DEFAULT_NODE_STYLE)
        ) {
          return item;
        }

        return {
          ...item,
          style: FLOW_DEFAULT_NODE_STYLE
        };
      })
    );
  }, [setNodes]);

  const updateNode = useCallback(dataAlter => {
    setNodes(old =>
      old.map(itemNode => {
        if (itemNode.id === dataAlter.id) {
          return dataAlter;
        }
        return itemNode;
      })
    );
    setModalAddText(null);
    setModalAddInterval(null);
    setModalAddMenu(null);
  }, [setNodes]);

  const actions = useMemo(() => [
    {
      icon: (
        <RocketLaunch
          sx={{
            color: "#3ABA38"
          }}
        />
      ),
      name: "Inicio",
      type: "start"
    },
    {
      icon: (
        <LibraryBooks
          sx={{
            color: "#EC5858"
          }}
        />
      ),
      name: "Conte\u00FAdo",
      type: "content"
    },
    {
      icon: (
        <DynamicFeed
          sx={{
            color: "#683AC8"
          }}
        />
      ),
      name: "Menu",
      type: "menu"
    },
    {
      icon: (
        <CallSplit
          sx={{
            color: "#1FBADC"
          }}
        />
      ),
      name: "Randomizador",
      type: "random"
    },
    {
      icon: (
        <AccessTime
          sx={{
            color: "#F7953B"
          }}
        />
      ),
      name: "Intervalo",
      type: "interval"
    },
    {
      icon: (
        <ConfirmationNumber
          sx={{
            color: "#F7953B"
          }}
        />
      ),
      name: "Ticket",
      type: "ticket"
    }
  ], []);

  const clickActions = useCallback(type => {
    switch (type) {
      case "start":
        addNode("start");
        break;
      case "menu":
        setModalAddMenu("create");
        break;
      case "content":
        setModalAddSingleBlock("create");
        break;
      case "random":
        setModalAddRandomizer("create");
        break;
      case "interval":
        setModalAddInterval("create");
        break;
      case "ticket":
        setModalAddTicket("create");
        break;
      default:
    }
    setSpeedDialOpen(false);
  }, [addNode]);

  /*
  const actions = [
    {
      icon: (
        <RocketLaunch
          sx={{
            color: "#3ABA38"
          }}
        />
      ),
      name: "Inicio",
      type: "start"
    },
    {
      icon: (
        <LibraryBooks
          sx={{
            color: "#EC5858"
          }}
        />
      ),
      name: "Conte\u00FAdo",
      type: "content"
    },
    {
      icon: (
        <DynamicFeed
          sx={{
            color: "#683AC8"
          }}
        />
      ),
      name: "Menu",
      type: "menu"
    },
    {
      icon: (
        <CallSplit
          sx={{
            color: "#1FBADC"
          }}
        />
      ),
      name: "Randomizador",
      type: "random"
    },
    {
      icon: (
        <AccessTime
          sx={{
            color: "#F7953B"
          }}
        />
      ),
      name: "Intervalo",
      type: "interval"
    },
    {
      icon: (
        <ConfirmationNumber
          sx={{
            color: "#F7953B"
          }}
        />
      ),
      name: "Ticket",
      type: "ticket"
    }
  ];
  */

  /*
  const clickActions = type => {
    switch (type) {
      case "start":
        addNode("start");
        break;
      case "menu":
        setModalAddMenu("create");
        break;
      case "content":
        setModalAddSingleBlock("create");
        break;
      case "random":
        setModalAddRandomizer("create");
        break;
      case "interval":
        setModalAddInterval("create");
        break;
      case "ticket":
        setModalAddTicket("create");
        break;
      default:
    }
    setSpeedDialOpen(false);
  };
  */

  return (
    <Stack sx={{ height: "100vh" }}>
      {modalAddText && (
        <FlowBuilderAddTextModal
          open={modalAddText}
          onSave={textAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddText}
        />
      )}
      {modalAddInterval && (
        <FlowBuilderIntervalModal
          open={modalAddInterval}
          onSave={intervalAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddInterval}
        />
      )}
      {modalAddCondition && (
        <FlowBuilderConditionModal
          open={modalAddCondition}
          onSave={conditionAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddCondition}
        />
      )}
      {modalAddMenu && (
        <FlowBuilderMenuModal
          open={modalAddMenu}
          onSave={menuAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddMenu}
        />
      )}
      {modalAddImg && (
        <FlowBuilderAddImgModal
          open={modalAddImg}
          onSave={imgAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddImg}
        />
      )}
      {modalAddAudio && (
        <FlowBuilderAddAudioModal
          open={modalAddAudio}
          onSave={audioAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddAudio}
        />
      )}
      {modalAddRandomizer && (
        <FlowBuilderRandomizerModal
          open={modalAddRandomizer}
          onSave={randomizerAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddRandomizer}
        />
      )}
      {modalAddVideo && (
        <FlowBuilderAddVideoModal
          open={modalAddVideo}
          onSave={videoAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddVideo}
        />
      )}
      {modalAddSingleBlock && (
        <FlowBuilderSingleBlockModal
          open={modalAddSingleBlock}
          onSave={singleBlockAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddSingleBlock}
        />
      )}
      {modalAddTicket && (
        <FlowBuilderTicketModal
          open={modalAddTicket}
          onSave={ticketAdd}
          data={dataNode}
          onUpdate={updateNode}
          close={setModalAddTicket}
        />
      )}
      <MainHeader>
        <Title>Desenhe seu fluxo</Title>
      </MainHeader>
      {!loading && (
        <Paper
          className={classes.mainPaper}
          variant="outlined"
          onScroll={handleScroll}
        >
          <Stack>
            <SpeedDial
              ariaLabel="Adicionar bloco ao fluxo"
              sx={{
                position: "absolute",
                top: 22,
                left: 22,
                zIndex: 20,
                ".MuiSpeedDial-fab": {
                  background: "linear-gradient(145deg, #0068ff 0%, #003fb3 100%)",
                  border: "1px solid rgba(219, 234, 254, 0.34)",
                  boxShadow:
                    "0 18px 30px rgba(0, 22, 58, 0.42), 0 0 0 6px rgba(0, 90, 229, 0.16), inset 0 1px 0 rgba(255,255,255,0.28)",
                  transition: "transform 150ms ease, box-shadow 150ms ease",
                  '&:hover': {
                    background: "linear-gradient(145deg, #1174ff 0%, #004bd6 100%)",
                    transform: "translateY(-1px)",
                    boxShadow:
                      "0 22px 34px rgba(0, 22, 58, 0.50), 0 0 0 7px rgba(0, 90, 229, 0.20), inset 0 1px 0 rgba(255,255,255,0.32)"
                  }
                },
                ".MuiSpeedDialAction-fab": {
                  border: "1px solid rgba(93, 170, 252, 0.22)",
                  boxShadow:
                    "0 10px 18px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.85)"
                },
                ".MuiSpeedDialAction-staticTooltipLabel": {
                  borderRadius: "8px",
                  padding: "7px 13px",
                  color: "#1e293b",
                  fontWeight: 700,
                  boxShadow: "0 8px 18px rgba(15,23,42,0.18)"
                }
              }}
              icon={<SpeedDialIcon />}
              direction={"down"}
              open={speedDialOpen}
              onOpen={(_, reason) => {
                if (reason === "toggle") {
                  setSpeedDialOpen(true);
                }
              }}
              onClose={(_, reason) => {
                if (reason === "toggle" || reason === "escapeKeyDown") {
                  setSpeedDialOpen(false);
                }
              }}
            >
              {actions.map(action => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.name}
                  tooltipOpen
                  tooltipPlacement={"right"}
                  onClick={() => clickActions(action.type)}
                />
              ))}
            </SpeedDial>
          </Stack>
          <Stack
            sx={{
              position: "absolute",
              justifyContent: "center",
              flexDirection: "row",
              width: "100%",
              zIndex: 5,
              pointerEvents: "none"
            }}
          >
            <Typography
              style={{
                color: "#eaf4ff",
                backgroundColor: "rgba(2, 16, 39, 0.58)",
                border: "1px solid rgba(93, 170, 252, 0.18)",
                borderRadius: "999px",
                boxShadow: "0 14px 28px rgba(0,0,0,0.24)",
                padding: "8px 18px",
                textShadow: "0 1px 10px rgba(93,170,252,0.44)",
                fontWeight: 700
              }}
            >
              Não se esqueça de salvar seu fluxo!
            </Typography>
          </Stack>
          <Stack direction={"row"} justifyContent={"end"} sx={{ position: "relative", zIndex: 6 }}>
            <Button
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 800,
                px: 3,
                background: "linear-gradient(145deg, #0068ff 0%, #0048c9 100%)",
                boxShadow:
                  "0 14px 24px rgba(0, 32, 90, 0.36), inset 0 1px 0 rgba(255,255,255,0.25)",
                "&:hover": {
                  background: "linear-gradient(145deg, #1174ff 0%, #005AE5 100%)",
                  boxShadow:
                    "0 18px 28px rgba(0, 32, 90, 0.42), 0 0 22px rgba(93,170,252,0.22)"
                }
              }}
              variant="contained"
              color="primary"
              onClick={() => saveFlow()}
            >
              Salvar
            </Button>
          </Stack>

          <Stack
            direction={"row"}
            style={{
              width: "100%",
              height: "90%",
              position: "relative",
              display: "flex"
            }}
          >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              deleteKeyCode={FLOW_DELETE_KEY_CODES}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeDoubleClick={doubleClick}
              onNodeClick={clickNode}
              onEdgeClick={clickEdge}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              fitView
              connectionLineStyle={FLOW_CONNECTION_LINE_STYLE}
              style={FLOW_CANVAS_STYLE}
              edgeTypes={edgeTypes}
              variant={"cross"}
              defaultEdgeOptions={FLOW_DEFAULT_EDGE_OPTIONS}
            >
              <Controls />
              {miniMapOpen ? (
                <div
                  className={`${classes.miniMapPanel} nodrag nopan`}
                  onMouseDown={event => event.stopPropagation()}
                >
                  <div className={classes.miniMapHeader}>
                    <span className={classes.miniMapLabel}>
                      <MapOutlined style={{ fontSize: 15 }} />
                      Mapa
                    </span>
                    <button
                      type="button"
                      className={classes.miniMapClose}
                      onClick={() => setMiniMapOpen(false)}
                    >
                      <Close style={{ fontSize: 16 }} />
                    </button>
                  </div>
                  <MiniMap
                    nodeColor="#5DAAFC"
                    nodeStrokeColor="#00f0ff"
                    nodeBorderRadius={4}
                    maskColor="rgba(0, 15, 38, 0.46)"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className={`${classes.miniMapToggle} nodrag nopan`}
                  onMouseDown={event => event.stopPropagation()}
                  onClick={() => setMiniMapOpen(true)}
                >
                  <MapOutlined style={{ fontSize: 16 }} />
                  Mapa
                </button>
              )}
              <Background variant="dots" gap={28} size={0.35} color="rgba(93,170,252,0.16)" />
            </ReactFlow>

            <Stack
              style={{
                backgroundColor: "rgba(6, 22, 44, 0.82)",
                height: "20px",
                width: "58px",
                position: "absolute",
                bottom: 0,
                right: 0,
                zIndex: 1111
              }}
            />
            {/* <Stack
                style={{
                  backgroundColor: "#1B1B1B",
                  height: "70%",
                  width: "150px",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  zIndex: 1111,
                  borderRadius: 3,
                  padding: 8
                }}
                spacing={1}
              >
                <Typography style={{ color: "#ffffff", textAlign: "center" }}>
                  Adicionar
                </Typography>
                <Button
                  onClick={() => addNode("start")}
                  variant="contained"
                  style={{
                    backgroundColor: "#3ABA38",
                    color: "#ffffff",
                    padding: 8,
                    "&:hover": {
                      backgroundColor: "#3e3b7f"
                    },
                    textTransform: "none"
                  }}
                >
                  <RocketLaunch
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Inicio
                </Button>
                <Button
                  onClick={() => setModalAddText("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#6865A5",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <Message
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Texto
                </Button>
                <Button
                  onClick={() => setModalAddInterval("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#F7953B",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <AccessTime
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Intervalo
                </Button>
                <Button
                  onClick={() => setModalAddCondition("create")}
                  variant="contained"
                  disabled
                  style={{
                    backgroundColor: "#524d4d",
                    color: "#cccaed",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <ImportExport
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Condição
                </Button>
                <Button
                  onClick={() => setModalAddMenu("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#683AC8",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <DynamicFeed
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Menu
                </Button>
                <Button
                  onClick={() => setModalAddAudio("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#6865A5",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <MicNone
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Audio
                </Button>
                <Button
                  onClick={() => setModalAddVideo("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#6865A5",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <Videocam
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Video
                </Button>
                <Button
                  onClick={() => setModalAddImg("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#6865A5",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <Image
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Imagem
                </Button>
                <Button
                  onClick={() => setModalAddRandomizer("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#1FBADC",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <CallSplit
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  Randomizador
                </Button>
                <Button
                  onClick={() => setModalAddSingleBlock("create")}
                  variant="contained"
                  style={{
                    backgroundColor: "#EC5858",
                    color: "#ffffff",
                    padding: 8,
                    textTransform: "none"
                  }}
                >
                  <LibraryBooks
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: "4px"
                    }}
                  />
                  {"Conte\u00FAdo"}
                </Button>
              </Stack> */}
          </Stack>
        </Paper>
      )}
      {loading && (
        <Stack justifyContent={"center"} alignItems={"center"} height={"70vh"}>
          <CircularProgress />
        </Stack>
      )}
    </Stack>
  );
};
